import { useState, type ReactNode } from 'react'
import { userFacingMessage } from '../../api/errors'
import { useOpenBillingPortal, useStartSubscription, type BillingStatus } from '../../api/queries/billing'
import { Button } from '../../ui/Button'
import { BuyCreditsDialog } from './BuyCreditsDialog'
import { formatMonthly } from './pricing'
import styles from './Billing.module.css'

type Kind = 'upgrade' | 'portal' | 'credits'

/**
 * The three things a user can do about their plan, shown only when the backend
 * says each one would work (`actions`). A subscriber does not see Upgrade — a
 * second checkout would bill them twice — and nobody sees any of it on a
 * deployment without Stripe keys. No control appears until it does something.
 *
 * `primary` names the green button. `'first'` makes it the leftmost one that is
 * offered, which is how the wall reads as a recommendation: one green button,
 * always, on the left, Cancel on the right (Mike, 2026-08-27). The order is
 * credits, upgrade, portal, so the recommended step is Buy credits when it is
 * possible, and Manage billing when only the card can fix things.
 */
export function PurchaseActions({ status, primary = 'upgrade', after }: { status: BillingStatus; primary?: Kind | 'first'; after?: ReactNode }) {
  const { actions, pricing } = status
  const [buying, setBuying] = useState(false)
  const subscribe = useStartSubscription()
  const portal = useOpenBillingPortal()

  if (!actions.billing_available) return null
  const pending = subscribe.isPending || portal.isPending
  const error = subscribe.error ?? portal.error

  const offered: Kind[] = [
    ...(actions.can_buy_credits ? (['credits'] as const) : []),
    ...(actions.can_upgrade ? (['upgrade'] as const) : []),
    ...(actions.can_manage_billing ? (['portal'] as const) : []),
  ]
  const green = primary === 'first' ? offered[0] : primary

  function render(kind: Kind) {
    const variant = kind === green ? 'primary' : 'default'
    if (kind === 'upgrade') {
      return (
        <Button key={kind} variant={variant} disabled={pending} onClick={() => subscribe.mutate(undefined, { onSuccess: ({ url }) => window.location.assign(url) })}>
          {subscribe.isPending ? 'Opening Stripe…' : `Upgrade to Solo · ${formatMonthly(pricing.solo_monthly_price_cents, pricing.currency)}`}
        </Button>
      )
    }
    if (kind === 'portal') {
      return (
        <Button key={kind} variant={variant} disabled={pending} onClick={() => portal.mutate(undefined, { onSuccess: ({ url }) => window.location.assign(url) })}>
          {portal.isPending ? 'Opening Stripe…' : 'Manage billing'}
        </Button>
      )
    }
    return (
      <Button key={kind} variant={variant} disabled={pending} onClick={() => setBuying(true)}>
        Buy credits
      </Button>
    )
  }

  return (
    <>
      <div className={styles.actions}>
        {offered.map(render)}
        {after}
      </div>
      {error ? <p className={styles.error} role="alert">{userFacingMessage(error)}</p> : null}
      {buying ? <BuyCreditsDialog pricing={pricing} onClose={() => setBuying(false)} /> : null}
    </>
  )
}
